import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../config/api';
import { Layers, Plus, Search, Edit, Trash2, Save, Loader2, ArrowLeft, RotateCcw } from 'lucide-react';
import toast from '../utils/toast';
import SearchBar from '../components/SearchBar';
import {
  masterAccentButton,
  masterHeaderCard,
  masterHeaderIcon,
  masterInput,
  masterInputLabel,
  masterPageShell,
  masterToolbar,
  masterPanel,
  masterPanelPadding,
  masterPrimaryButton,
  masterSearchInput,
  masterSearchWrap,
  masterSelect,
  masterSubtitle,
  masterTitle,
  masterTableData
} from '../components/masterPageStyles';

const TYPE_CACHE_KEY = 'type-master-options';
const CATEGORY_TYPE_SELECTION_KEY = 'medicine-category-selected-type';

const emptyForm = {
  category_id: '',
  category_name: '',
  type: '',
  description: '',
  status: 'Active'
};

const MedicineCategoryMaster = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [formData, setFormData] = useState(emptyForm);
  const [showForm, setShowForm] = useState(true);
  const [typeOptions, setTypeOptions] = useState([]);

  const getStoredTypeSelection = () => {
    if (typeof window === 'undefined') return '';
    return localStorage.getItem(CATEGORY_TYPE_SELECTION_KEY) || '';
  };

  const readCachedTypeOptions = () => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = localStorage.getItem(TYPE_CACHE_KEY);
      const parsed = JSON.parse(raw || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  };

  const persistTypeOptions = (options) => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(TYPE_CACHE_KEY, JSON.stringify(options));
  };

  const loadTypeOptions = async () => {
    const cached = readCachedTypeOptions();
    if (cached.length) setTypeOptions(cached);

    try {
      const res = await api.get('/master/type/all');
      if (res.data?.success) {
        const options = (res.data.data || [])
          .filter((item) => String(item.status || 'Active') === 'Active')
          .map((item) => {
            const value = String(item.typeName || '').toUpperCase();
            return { value, label: value };
          });
        setTypeOptions(options);
        persistTypeOptions(options);
      }
    } catch (_) {}
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await api.get('/master/medicine-category/all');
      if (res.data.success) setItems(res.data.data || []);
    } catch (err) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchNextId = async () => {
    try {
      const res = await api.get('/master/medicine-category/latest-id');
      if (res.data?.success) {
        setFormData((prev) => ({ ...prev, category_id: res.data.nextId }));
      }
    } catch (_) {}
  };

  useEffect(() => {
    fetchItems();
    loadTypeOptions();
  }, []);

  const filteredData = useMemo(() => {
    const sortedList = [...items].sort((a, b) => {
      const idA = String(a.category_id || a._id || '');
      const idB = String(b.category_id || b._id || '');
      return idB.localeCompare(idA, undefined, { numeric: true, sensitivity: 'base' });
    });

    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedList;

    return sortedList.filter((item) => {
      const categoryId = (item.category_id || '').toLowerCase();
      const categoryName = (item.category_name || '').toLowerCase();
      const type = (item.type || '').toLowerCase();

      return categoryId.startsWith(q) || categoryName.startsWith(q) || type.startsWith(q);
    });
  }, [items, searchTerm]);

  const paginatedData = useMemo(() => {
    if (pageSize === -1) return filteredData;
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, items.length, pageSize]);

  const handleEdit = (item) => {
    setEditId(item._id);
    setFormData({
      category_id: item.category_id,
      category_name: item.category_name,
      type: item.type || '',
      description: item.description || '',
      status: item.status || 'Active'
    });
    if (typeof window !== 'undefined') {
      localStorage.setItem(CATEGORY_TYPE_SELECTION_KEY, item.type || '');
    }
    setShowForm(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'type' && typeof window !== 'undefined') {
      localStorage.setItem(CATEGORY_TYPE_SELECTION_KEY, value);
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const payload = { ...formData, id: editId };
      await api.post('/master/medicine-category/upsert', payload);
      toast.success(editId ? 'Updated' : 'Saved');
      setEditId(null);
      setFormData({ ...emptyForm, type: getStoredTypeSelection() });
      setShowForm(true);
      fetchItems();
    } catch (err) {
      const msg = err.response?.data?.message || 'Process failed';
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await api.delete(`/master/medicine-category/${id}`);
      toast.success('Deleted');
      fetchItems();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className={`${masterPageShell} p-1`}>
      <header className={`${masterHeaderCard} mb-1`}>
        <div className="flex items-center gap-1">
          <div className={masterHeaderIcon}><Layers size={10} /></div>
          <div>
            <h1 className={masterTitle}>Medicine Category Master</h1>
            <p className={masterSubtitle}>Catalog Grouping</p>
          </div>
        </div>

        {!showForm ? (
          <button
            onClick={async () => {
              setEditId(null);
              setFormData({ ...emptyForm, type: getStoredTypeSelection() });
              setShowForm(true);
              await fetchNextId();
            }}
            className={masterAccentButton}
          >
            <Plus size={14} /> Add Category
          </button>
        ) : (
          <button
            onClick={() => setShowForm(false)}
            className={masterPrimaryButton}
          >
            <ArrowLeft size={14} /> VIEW LIST
          </button>
        )}
      </header>

      <main className="mx-auto max-w-7xl">
        {showForm ? (
          <form onSubmit={handleSubmit} className={`${masterPanelPadding} space-y-1`}>
            <div className="flex items-center justify-between border-b pb-1">
              <h4 className="text-sm font-bold text-slate-800 uppercase">{editId ? 'Edit Category' : 'New Category'}</h4>
              <button
                type="button"
                onClick={async () => {
                  setFormData((prev) => ({ ...emptyForm, category_id: prev.category_id, type: getStoredTypeSelection() }));
                  await fetchNextId();
                }}
                className="text-[10px] font-bold text-rose-500 flex items-center gap-1 uppercase hover:underline"
              >
                <RotateCcw size={12}/> Reset
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-1 gap-y-1">
              {/* <Input label="Category ID" name="category_id" value={formData.category_id} readOnly /> */}
               <Select
                label="Type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                required
                placeholder="Select Type"
                options={typeOptions}
              />
              <Input label="Category Name" name="category_name" value={formData.category_name} onChange={handleChange} required />
             
              {/* <Input label="Description" name="description" value={formData.description} onChange={handleChange} /> */}
              <Select label="Status" name="status" value={formData.status} onChange={handleChange} options={['Active', 'Inactive']} />
            </div>

            <div className="pt-1 border-t flex justify-end">
              <button disabled={isSaving} type="submit" className={`${masterAccentButton} px-4 py-1`}>
                {isSaving ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>}
                {editId ? 'Update' : 'Save'} 
              </button>
            </div>
          </form>
        ) : (
          <div className="max-w-7xl mx-auto bg-white rounded-xl border border-slate-400 overflow-hidden shadow-sm">
                   <div>
              <SearchBar 
                value={searchTerm} 
                onChange={(val) => {
                  setSearchTerm(val);
                  setCurrentPage(1);
                }} 
                placeholder="Search alphabetically by category ID, name, or type..." 
                totalItems={filteredData.length} 
                currentPage={currentPage} 
                pageSize={pageSize} 
                onPageChange={setCurrentPage} 
                onPageSizeChange={setPageSize} 
              />
            </div>
            
        <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left">
              <thead>
                  <tr>
                    <th className={masterTableData}>S.No</th>
                    <th className={masterTableData}>ID</th>
                     <th className={masterTableData}>Type</th>
                    <th className={masterTableData}>Category</th>
                   
                    {/* <th className={masterTableData}>Description</th> */}
                    <th className={masterTableData}>Status</th>
                    <th className={masterTableData}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-300 font-bold text-slate-600">
                  {loading ? (
                    <tr><td colSpan="6" className="p-12 text-center">Syncing Data...</td></tr>
                  ) : paginatedData.length > 0 ? paginatedData.map((item, index) => (
                    <tr key={item._id} className="hover:bg-emerald-50/30">
                      <td className={masterTableData}>{pageSize === -1 ? index + 1 : (currentPage - 1) * pageSize + index + 1}</td>
                      <td className={masterTableData}>{item.category_id}</td>
                      <td className={masterTableData} title={item.type || '-'}>
                        {item.type || '-'}
                      </td>
                      <td className={masterTableData} title={item.category_name}>
                        {item.category_name}
                      </td>
                      {/* <td className={masterTableData} title={item.description || '-'}>
                        {item.description || '-'}
                      </td> */}
                      <td className={masterTableData}>
                        <span className={`px-2 py-0.5 rounded text-[9px] ${item.status === 'Active' ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'}`}>
                          {item.status}
                        </span>
                      </td>
                     <td className={masterTableData}>
                        <div className="flex justify-center gap-1">
                          <button onClick={() => handleEdit(item)} className="p-1 text-emerald-600 rounded-lg hover:bg-emerald-100"><Edit size={14} /></button>
                          <button onClick={() => handleDelete(item._id)} className="p-1 text-rose-600 rounded-lg hover:bg-rose-100"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="6" className="p-12 text-center text-slate-300 font-semibold uppercase text-xs">No matching records found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const Input = ({ label, required, ...props }) => (
  <div className="flex flex-col ">
    <label className={masterInputLabel}>{label}</label>
    <input required={required} {...props} className={masterInput} />
  </div>
);

const Select = ({ label, options, ...props }) => (
  <div className="flex flex-col">
    <label className={masterInputLabel}>{label}</label>
    <select {...props} className={masterSelect}>
      {props.placeholder ? <option value="">{props.placeholder}</option> : null}
      {options.map((opt) => {
        const value = typeof opt === 'string' ? opt : opt.value;
        const labelValue = typeof opt === 'string' ? opt : opt.label;
        return <option key={value} value={value}>{labelValue}</option>;
      })}
    </select>
  </div>
);

export default MedicineCategoryMaster;
